import { gql } from '@apollo/client/core';
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';

const ADD_WAIVER = gql`
  mutation AddCourseOutstandingWaiver($input: AddCourseOutstandingWaiverInput!) {
    addCourseOutstandingWaiver(input: $input) {
      success
      message
      code
    }
  }
`;

const BULK_ADD_WAIVER = gql`
  mutation BulkAddCourseOutstandingWaiver($input: BulkAddCourseOutstandingWaiverInput!) {
    bulkAddCourseOutstandingWaiver(input: $input) {
      success
      message
      code
    }
  }
`;

const DELETE_WAIVER = gql`
  mutation DeleteCourseOutstandingWaiver($id: ID!) {
    deleteCourseOutstandingWaiver(id: $id) {
      success
      message
      code
    }
  }
`;

const BULK_DELETE_WAIVER = gql`
  mutation BulkDeleteCourseOutstandingWaiver($ids: [ID!]!) {
    bulkDeleteCourseOutstandingWaiver(ids: $ids) {
      success
      message
      code
    }
  }
`;

export function useCourseOutstandingWaiverMutations() {
  const toast = useToast();

  const { mutate: addMutate, loading: adding } = useMutation(ADD_WAIVER);
  const { mutate: bulkAddMutate, loading: bulkAdding } = useMutation(BULK_ADD_WAIVER);
  const { mutate: deleteMutate, loading: deleting } = useMutation(DELETE_WAIVER);
  const { mutate: bulkDeleteMutate, loading: bulkDeleting } = useMutation(BULK_DELETE_WAIVER);

  async function addWaiver(input: { student_id: string; course_id: string; academic_calendar_id: string; reason?: string }) {
    try {
      const res = await addMutate({ input });
      const data = res?.data?.addCourseOutstandingWaiver;
      if (data?.success) {
        toast.add({ severity: 'success', summary: 'Waiver Added', detail: data.message, life: 3000 });
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: data?.message || 'Failed to add waiver', life: 5000 });
      }
      return data;
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function bulkAddWaivers(input: { student_ids: string[]; course_ids: string[]; academic_calendar_id: string; reason?: string }) {
    try {
      const res = await bulkAddMutate({ input });
      const data = res?.data?.bulkAddCourseOutstandingWaiver;
      if (data?.success) {
        toast.add({ severity: 'success', summary: 'Waivers Added', detail: data.message, life: 3000 });
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: data?.message || 'Failed to add waivers', life: 5000 });
      }
      return data;
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function deleteWaiver(id: string) {
    try {
      const res = await deleteMutate({ id });
      const data = res?.data?.deleteCourseOutstandingWaiver;
      if (data?.success) {
        toast.add({ severity: 'success', summary: 'Waiver Removed', detail: data.message, life: 3000 });
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: data?.message || 'Failed to remove waiver', life: 5000 });
      }
      return data;
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  async function bulkDeleteWaivers(ids: string[]) {
    try {
      const res = await bulkDeleteMutate({ ids });
      const data = res?.data?.bulkDeleteCourseOutstandingWaiver;
      if (data?.success) {
        toast.add({ severity: 'success', summary: 'Waivers Removed', detail: data.message, life: 3000 });
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: data?.message || 'Failed to remove waivers', life: 5000 });
      }
      return data;
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
      return null;
    }
  }

  return {
    addWaiver,
    bulkAddWaivers,
    deleteWaiver,
    bulkDeleteWaivers,
    adding,
    bulkAdding,
    deleting,
    bulkDeleting,
  };
}
